import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, Info, AlertTriangle, Flag } from "lucide-react";
import { LOGO_URL } from "../utils/brand";
// eslint-disable-next-line
const APP_VERSION = require("../../package.json").version;

export default function PrivacyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      {/* Top nav — matches Learn More styling */}
      <nav className="sticky top-0 z-40 bg-slate-950/85 backdrop-blur-md border-b border-slate-800/70">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors"
            data-testid="privacy-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <img
            src={LOGO_URL}
            alt="FEC Watchdog"
            className="h-8 w-auto object-contain select-none"
            draggable={false}
          />
          <Link
            to="/login"
            className="text-sm text-sky-300 hover:text-white transition-colors"
          >
            Sign In
          </Link>
        </div>
      </nav>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-12" data-testid="privacy-content">
        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-sky-400 bg-clip-text text-transparent tracking-tight">
          Copyright & Privacy
        </h1>
        <p className="text-slate-400 mt-2 text-sm">Effective 2026 &middot; FEC Watchdog v{APP_VERSION}</p>

        <section className="mt-10 space-y-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-sky-400 mt-0.5 shrink-0" />
            <h2 className="text-xl font-semibold">Analysis, not authorship</h2>
          </div>
          <p className="text-slate-300 leading-relaxed">
            FEC Watchdog is an <b>analysis platform</b>. It does not create the underlying campaign-finance
            data. All original content shown on this site &mdash; contributor names, committee names,
            addresses, dates, contribution amounts, and related fields &mdash; <b>belongs to and was
            supplied by third parties</b>, including the Federal Election Commission (FEC) and
            other public sources uploaded by our users.
          </p>
          <p className="text-slate-300 leading-relaxed">
            We do not claim copyright over the underlying records. Our contribution is limited to the
            visualisations, aggregations, cluster maps, geocoding, anomaly detection, and other analytical
            outputs produced by this software.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
            <h2 className="text-xl font-semibold">Public data &mdash; private people</h2>
          </div>
          <p className="text-slate-300 leading-relaxed">
            Federal campaign contributions are, by law, a matter of <b>public record</b>. That's why the
            data can appear here at all. Still, real people stand behind every row. We ask that you
            treat the information visible on this site with judgment and respect:
          </p>
          <ul className="list-disc list-inside space-y-1.5 text-slate-300 pl-4">
            <li>Do not harass, threaten, or intimidate anyone based on what you find here.</li>
            <li>Do not misrepresent this analysis as an official government finding.</li>
            <li>Do not export or redistribute personal details out of context.</li>
            <li>Verify anything unusual against the primary FEC source before drawing conclusions.</li>
          </ul>
        </section>

        <section className="mt-10 space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <h2 className="text-xl font-semibold">Found something that looks wrong?</h2>
          </div>
          <p className="text-slate-300 leading-relaxed">
            If you detect fraud, an abnormality, or a pattern that warrants investigation, please:
          </p>
          <ol className="list-decimal list-inside space-y-1.5 text-slate-300 pl-4">
            <li><b>Document it inside this tool</b> &mdash; take screenshots of the map, save the address details, note the dataset and time range.</li>
            <li><b>Submit your findings to your local election office</b> or state Board of Elections. They are the authoritative body for review.</li>
            <li>Where appropriate, also file a complaint directly with the FEC at <a href="https://www.fec.gov/help-candidates-and-committees/filing-reports/complaints-about-committees/" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:text-sky-300 underline underline-offset-2">fec.gov</a>.</li>
          </ol>
          <div className="mt-4 flex items-start gap-3 p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <Flag className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-100/90">
              This platform assists your review. It is not a substitute for the formal election
              integrity process. Only the appropriate election authority can act on suspected
              violations.
            </p>
          </div>
        </section>

        <section className="mt-12 pt-8 border-t border-slate-800 text-xs text-slate-500 leading-relaxed">
          <p>
            &copy; 2026 FEC Watchdog. This site is a private analytical tool and is not affiliated
            with, endorsed by, or sponsored by the Federal Election Commission or any political party,
            candidate, or committee. All trademarks are the property of their respective owners.
          </p>
        </section>
      </main>
    </div>
  );
}
